/**
 * Selbstdiagnose der Realtime-Schicht (#1035).
 *
 * Alex, 2026-08-11, nach fünf Tagen an Arcturian: Wenn etwas fehlschlägt, soll
 * der Agent es **selbst** als Issue melden — mit Ursache, Absicht und Kontext —
 * damit die Liste abgearbeitet und über mehrere Installationen hinweg
 * konsolidiert werden kann. Bis heute war er der einzige Integrationstest:
 * Jeder Defekt landete zuerst in seinem Mund statt in einem Bericht.
 *
 * DIE AUFTEILUNG IST DER KERN. Nicht „das Modell meldet alles": Ein Modell,
 * dessen Ausgabe gerade verworfen wurde, ist genau das, dem man den Bericht
 * darüber am wenigsten anvertrauen sollte. Deshalb:
 *
 *   - Diese Datei erzeugt den Bericht aus **Tatsachen**, die der Client ohnehin
 *     hat — deterministisch, ohne Modell, ohne Token, nicht selbst kaputtgehbar.
 *   - Die **Absicht** („der Bediener wollte wissen, was Updates aus bedeutet")
 *     ergänzt das Modell später als ein Satz. Fehlt sie, wird trotzdem gemeldet.
 *
 * Reine Funktionen ohne Netz und ohne Zeitquelle im Inneren, damit sich das
 * Verhalten prüfen lässt statt es im Fehlerpfad zu vermuten — dort, wo es nur
 * dann läuft, wenn ohnehin schon etwas schiefgegangen ist.
 */

/** Was der Client über einen Fehlschlag sicher weiß. */
export interface RealtimeFailure {
  /** Genauer Code, z. B. `invalid_resolver_payload_missing_target_kind`. */
  code: string;
  /** Betriebsart der Sitzung (`arcturian`, `agentos-narrator`, …). */
  mode: string;
  /** Aushandlungsstand, z. B. `agentos.arcturian-action.v2`. */
  contract?: string;
  /** Die verletzte Regel im Klartext — Feldnamen, nie Gesprochenes. */
  rule?: string;
  /** Transportkennung der Sitzung, für die Zuordnung im Serverprotokoll. */
  voiceSessionId?: string;
  /** Ausgelieferter Stand, damit „gilt das noch?" beantwortbar bleibt. */
  build?: string;
  /** Sprachdienst, mit dem gesprochen wurde. */
  endpoint?: string;
}

/** Ein fertiger, absendbarer Bericht. */
export interface RealtimeSelfReport {
  title: string;
  description: string;
  component: string;
  severity: 'critical' | 'major' | 'minor' | 'cosmetic';
  /** Zusammenführungsschlüssel — gleicher Abdruck heißt: derselbe Defekt. */
  fingerprint: string;
}

export const SELF_REPORT_COMPONENT = 'arcturian-realtime';

/** Die Ablage, die der Bericht braucht — als Schnittstelle, damit die
 * Zusammenführung prüfbar ist, ohne die echte Liste vollzuschreiben. */
export interface SelfReportSink {
  openByComponent(component: string): Promise<Array<{
    id: number;
    metadata_json?: Record<string, unknown> | null;
  }>>;
  create(body: {
    title: string;
    description: string;
    component: string;
    severity: string;
    reporter: string;
    reporter_type: string;
    tags?: string[];
    metadata_json?: Record<string, unknown>;
  }): Promise<{ id: number }>;
  comment(id: number, body: string): Promise<unknown>;
}

export type SelfReportOutcome =
  | { outcome: 'created'; id: number }
  | { outcome: 'commented'; id: number }
  | { outcome: 'failed'; reason: string };

/**
 * Legt den Bericht ab — als Kommentar, wenn derselbe Abdruck schon offen liegt,
 * sonst als neuer Eintrag.
 *
 * ZWEI EIGENSCHAFTEN SIND HIER WICHTIGER ALS DER ABLAUF:
 *
 * 1. **Zusammenführung über den Abdruck, nicht über den Wortlaut.** Alex' Absicht
 *    war ausdrücklich, Einträge über Installationen und Menschen hinweg
 *    konsolidieren zu können. Ein Titelvergleich träfe das nicht: derselbe Defekt
 *    heißt bei zwei Bedienern gleich, aber zwei verschiedene Defekte können es
 *    auch. Der Abdruck steht deshalb in `metadata_json`, wo er exakt vergleichbar
 *    bleibt, statt in einer Volltextsuche zerlegt zu werden.
 *
 * 2. **Diese Funktion wirft nie.** Sie läuft ausschließlich in einem Pfad, in dem
 *    schon etwas schiefgegangen ist. Ein Meldeweg, der beim Melden scheitert und
 *    dabei den Fehlerpfad mitreißt, macht aus einem erklärbaren Fehlschlag einen
 *    unerklärlichen — genau das Gegenteil dessen, wofür er gebaut wurde.
 */
export async function submitSelfReport(
  report: RealtimeSelfReport,
  sink: SelfReportSink,
  reporter: string,
): Promise<SelfReportOutcome> {
  try {
    const open = await sink.openByComponent(report.component);
    const existing = open.find((item) => item.metadata_json?.fingerprint === report.fingerprint);
    if (existing) {
      await sink.comment(existing.id, `**Erneut aufgetreten.**\n\n${report.description}`);
      return { outcome: 'commented', id: existing.id };
    }
    const created = await sink.create({
      title: report.title,
      description: report.description,
      component: report.component,
      severity: report.severity,
      reporter,
      reporter_type: 'agent',
      tags: ['selbstdiagnose', 'realtime'],
      metadata_json: { fingerprint: report.fingerprint, self_reported: true },
    });
    return { outcome: 'created', id: created.id };
  } catch (cause) {
    return { outcome: 'failed', reason: cause instanceof Error ? cause.message : 'unbekannt' };
  }
}

/**
 * Der Zusammenführungsschlüssel.
 *
 * Bewusst aus **Code + Betriebsart + Vertrag** — und ausdrücklich NICHT aus dem
 * Inhalt, der Sitzung oder der Uhrzeit. Nur so trifft derselbe Defekt bei
 * verschiedenen Menschen denselben Eintrag; nähme man den Inhalt dazu, entstünde
 * pro Vorfall ein neues Issue und die Liste würde wertlos.
 */
export function selfReportFingerprint(failure: RealtimeFailure): string {
  return [failure.code, failure.mode, failure.contract || 'kein-vertrag']
    .map((part) => String(part).trim().toLowerCase().replace(/\s+/g, '-'))
    .join('|');
}

/**
 * Wann ein Fehlschlag gemeldet werden darf.
 *
 * Ohne Bremse erzeugt eine hängende Schleife in Minuten hunderte Einträge —
 * und ausgerechnet dann, wenn das System ohnehin nicht funktioniert. Derselbe
 * Abdruck darf deshalb innerhalb des Fensters nur einmal melden; alles Weitere
 * ist ein Vorkommen am bestehenden Eintrag, kein neues Issue.
 */
export const SELF_REPORT_WINDOW_MS = 10 * 60 * 1000;

export function shouldSelfReport(
  fingerprint: string,
  lastSeen: Map<string, number>,
  nowMs: number,
  windowMs = SELF_REPORT_WINDOW_MS,
): boolean {
  const previous = lastSeen.get(fingerprint);
  return previous == null || nowMs - previous >= windowMs;
}

/**
 * Entfernt, was nicht in ein Issue gehört.
 *
 * Der Grund gehört ins Protokoll, das Gesagte nicht — dieselbe Grenze wie bei
 * den Verwerfungsgründen. Ein Bericht darf einen Feldnamen nennen („target_kind
 * fehlte"), niemals den Satz, den Alex gesprochen hat. Diese Funktion ist der
 * einzige Weg, auf dem Freitext in die Beschreibung gelangt.
 */
export function redactForReport(text: string | undefined, maxLength = 200): string {
  if (!text) return '';
  return text
    .replace(/[\r\n]+/g, ' ')
    .replace(/\b[\w.+-]+@[\w-]+\.[\w.]+\b/g, '[Adresse]')
    .replace(/\b(?:eyJ|sk-|Bearer\s+)\S+/gi, '[Zugangsdaten]')
    .replace(/:[^:@\s/]*@/g, ':***@')
    .trim()
    .slice(0, maxLength);
}

/**
 * Baut den Bericht.
 *
 * `intent` ist die einzige Angabe, die vom Modell stammen darf, und sie ist
 * optional: Ein Bericht ohne Absicht ist unvollständig, ein fehlender Bericht
 * ist nutzlos.
 */
export function buildSelfReport(
  failure: RealtimeFailure,
  options: { intent?: string; occurredAtIso: string } ,
): RealtimeSelfReport {
  const fingerprint = selfReportFingerprint(failure);
  const intent = redactForReport(options.intent);
  const rule = redactForReport(failure.rule, 300);

  const facts = [
    `**Fehlercode:** \`${failure.code}\``,
    `**Betriebsart:** ${failure.mode}`,
    failure.contract ? `**Vertrag:** ${failure.contract}` : '',
    rule ? `**Verletzte Regel:** ${rule}` : '',
    failure.build ? `**Build:** ${failure.build}` : '',
    failure.voiceSessionId ? `**Sitzung:** \`${failure.voiceSessionId}\`` : '',
    failure.endpoint ? `**Sprachdienst:** ${failure.endpoint}` : '',
    `**Zeitpunkt:** ${options.occurredAtIso}`,
  ].filter(Boolean).join('\n');

  const intentBlock = intent
    ? `\n\n## Absicht des Bedieners\n\n${intent}`
    : '\n\n## Absicht des Bedieners\n\nNicht erfasst — das Modell hat sie nicht mitgeliefert. Der Bericht steht trotzdem, weil die Tatsachen unabhängig davon gelten.';

  return {
    title: `Realtime-Fehlschlag: ${failure.code} (${failure.mode})`,
    component: SELF_REPORT_COMPONENT,
    severity: 'major',
    fingerprint,
    description: `Automatisch gemeldet von der Realtime-Schicht (#1035).

## Tatsachen

${facts}${intentBlock}

## Zusammenführung

**Abdruck:** \`${fingerprint}\`

Gleicher Abdruck bedeutet denselben Defekt — unabhängig davon, wer ihn erlebt
hat. Weitere Vorkommen gehören als Kommentar an diesen Eintrag, nicht als neues
Issue.

## Hinweis zur Lesart

Diese Beschreibung enthält bewusst **keinen gesprochenen Inhalt**, nur
Feldnamen und Regeln. Wer den Wortlaut braucht, findet ihn über die
Sitzungskennung im Diagnose-Protokoll — sofern es eingeschaltet war.`,
  };
}
